import type { FC_FaceBBox } from './face.dto';
import { PredefinedFileTags } from './photo.dto';

// Wire values match backend PhotographyType; the predicate stays framework- and backend-libs-free.
export const SOLO_CONTENT_TYPES: readonly string[] = ['portrait', 'individual'];
const GROUP_CONTENT_TYPE: string = 'group';

export interface SoloFrameSignals {
  /** Content verdict of the frame ('portrait' | 'individual' | 'group'); absent for legacy files. */
  photographyType?: string | null;
  /** Recognized faces on the frame; undefined = faces not computed (NOT the same as zero). */
  faceCount?: number | null;
  /** File tags: derived content marks (individual/group) and intent marks (portrait/student/teacher). */
  tags?: readonly string[] | null;
}

/**
 * The one truth table for «is this frame a solo shot». Content beats intent; the raw PORTRAIT
 * intent mark decides only for legacy files with no content signal at all.
 */
export function isSoloFrame(signals: SoloFrameSignals): boolean {
  const { photographyType, faceCount, tags } = signals;

  // Ноль лиц — не соло при любых пометках: базовый тип нуля лиц у распознавания — individual.
  if (faceCount === 0) {
    return false;
  }

  if (photographyType === GROUP_CONTENT_TYPE) {
    return false;
  }

  if (photographyType != null && SOLO_CONTENT_TYPES.includes(photographyType)) {
    return true;
  }

  if (typeof faceCount === 'number') {
    return faceCount === 1;
  }

  // Tag-level signals: derived content marks first (GROUP wins over a stray INDIVIDUAL), intent last.
  if (tags?.includes(PredefinedFileTags.GROUP)) {
    return false;
  }

  if (tags?.includes(PredefinedFileTags.INDIVIDUAL)) {
    return true;
  }

  return tags?.includes(PredefinedFileTags.PORTRAIT) ?? false;
}

/**
 * Метки сами решают вопрос «соло?» — это те три, которые читает `isSoloFrame`. Зовут отсюда те, у кого
 * счёт лиц НЕ точный (в списке лиц шлюза остаются ложные детекты): на размеченном кадре метка главнее.
 */
export function tagsDecideSolo(tags: readonly string[] | null | undefined): boolean {
  return (
    tags?.some(
      tag => tag === PredefinedFileTags.GROUP || tag === PredefinedFileTags.INDIVIDUAL || tag === PredefinedFileTags.PORTRAIT,
    ) ?? false
  );
}

/**
 * Портрет без найденного лица: `no-faces` и `individual`, не групповой. Только `individual` — его спрашивает
 * выбор «Портреты» при замене фото; кадр с одним `portrait` туда не попадает.
 */
export function isFacelessPortrait(tags: readonly string[] | null | undefined): boolean {
  return (
    !!tags?.includes(PredefinedFileTags.NO_FACES) &&
    tags.includes(PredefinedFileTags.INDIVIDUAL) &&
    !tags.includes(PredefinedFileTags.GROUP)
  );
}

/** Кадр «всё фото»: лица не нашлось, человека отметили на снимке целиком — рамка на весь кадр, это не лицо. */
export function isWholePhotoFace(face: { readonly relativeBbox?: FC_FaceBBox | null }): boolean {
  const box = face.relativeBbox;
  return !!box && box.x1 <= 0 && box.y1 <= 0 && box.x2 >= 1 && box.y2 >= 1;
}

/** File-level content verdict from its faces: the recognizer marks the subject PORTRAIT, the rest base-type. */
export function resolveContentTypeFromFaces(
  faces: ReadonlyArray<{ photographyType?: string | null }> | undefined | null,
): string | undefined {
  if (!faces?.length) {
    return undefined;
  }

  let firstKnown: string | undefined;
  for (const face of faces) {
    if (face.photographyType === 'portrait') {
      return 'portrait';
    }

    if (firstKnown === undefined && face.photographyType != null) {
      firstKnown = face.photographyType;
    }
  }

  return firstKnown;
}
